/**
 * Data Table Module
 *
 * Handles data table interactions: select all, row selection, and delete confirmations.
 * Auto-initializes on tables with data-controller="data-table".
 *
 * Usage in Twig:
 * <table data-controller="data-table" data-delete-url="/products">
 *     <input type="checkbox" data-select-all>
 *     <input type="checkbox" class="row-checkbox" value="123">
 *     <button data-delete-row data-id="123" data-name="Product Name">Delete</button>
 * </table>
 */

import { $, $$, on, ready } from '../utils/dom.js';
import { showNotification } from './notifications.js';

/**
 * Initialize data table functionality on a container
 * @param {Element} table - The table element
 */
function initDataTable(table) {
    const selectAllCheckbox = $('[data-select-all], #select-all', table) ||
                              table.closest('[data-controller="data-table"]')?.querySelector('[data-select-all]');
    const deleteUrl = table.dataset.deleteUrl || '';

    /**
     * Get all row checkboxes
     */
    function getRowCheckboxes() {
        return $$('.row-checkbox, [data-row-checkbox]', table);
    }

    /**
     * Update select-all checkbox state based on row checkboxes
     */
    function updateSelectAllState() {
        if (!selectAllCheckbox) return;

        const checkboxes = getRowCheckboxes();
        const checkedCount = checkboxes.filter(cb => cb.checked).length;

        selectAllCheckbox.checked = checkboxes.length > 0 && checkedCount === checkboxes.length;
        selectAllCheckbox.indeterminate = checkedCount > 0 && checkedCount < checkboxes.length;
    }

    /**
     * Toggle all checkboxes
     * @param {boolean} checked - Whether to check or uncheck
     */
    function toggleAll(checked) {
        getRowCheckboxes().forEach(cb => cb.checked = checked);
        updateSelectAllState();
    }

    /**
     * Get selected row IDs
     * @returns {string[]} Array of selected IDs
     */
    function getSelectedIds() {
        return getRowCheckboxes()
            .filter(cb => cb.checked)
            .map(cb => cb.value);
    }

    // Select all checkbox handler
    if (selectAllCheckbox) {
        selectAllCheckbox.addEventListener('change', function() {
            toggleAll(this.checked);
        });
    }

    // Row checkbox change handler
    on(table, 'change', '.row-checkbox, [data-row-checkbox]', () => {
        updateSelectAllState();
    });

    // Expose functions globally for legacy onclick handlers
    table._dataTable = {
        toggleAll,
        getSelectedIds,
        updateSelectAllState
    };
}

/**
 * Toggle all checkboxes in a table (legacy global function)
 * @param {HTMLInputElement} checkbox - The select-all checkbox
 */
window.toggleAllCheckboxes = function(checkbox) {
    const table = checkbox.closest('table') || checkbox.closest('[data-controller="data-table"]');
    if (table && table._dataTable) {
        table._dataTable.toggleAll(checkbox.checked);
    } else {
        // Fallback for tables not initialized with module
        const checkboxes = document.querySelectorAll('.row-checkbox');
        checkboxes.forEach(cb => cb.checked = checkbox.checked);
    }
};

/**
 * Confirm delete action (legacy global function)
 * @param {string|number} id - Item ID
 * @param {string} name - Item name
 */
window.confirmDelete = function(id, name) {
    if (confirm('Are you sure you want to delete "' + name + '"?')) {
        // Try to find delete URL from table
        const table = document.querySelector('[data-controller="data-table"]');
        const deleteUrl = table?.dataset.deleteUrl;

        if (deleteUrl) {
            // Use ajax-delete module if available
            if (window.deleteItem) {
                window.deleteItem(`${deleteUrl}/${id}`, { name, resource: 'item' });
            } else {
                // Fallback to form submission
                window.submitDelete(`${deleteUrl}/${id}`);
            }
        } else {
            console.log('Delete item with ID:', id);
            showNotification('Delete URL not configured', 'warning');
        }
    }
};

/**
 * Auto-initialize on page load
 */
ready(() => {
    $$('[data-controller="data-table"], table.data-table').forEach(initDataTable);
});

/**
 * Manual initialization
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        initDataTable(element);
    }
}

export default {
    init
};
